import Permissions from '@/common/models/memberships/Permissions';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import rootStore from '@/store';
import LocalCache from '@/utils/LocalCache';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const makeAction =
  (type) =>
  ({ commit }, ...args) =>
    commit(type, ...args);

const actions = {
  // markAsAuthenticated: makeAction(types.MARK_AS_AUTHENTICATED),
  markAsLoggedOut: makeAction(types.MARK_AS_LOGGED_OUT),
  updateUser({ commit }, user) {
    commit(types.UPDATE_USER, user);
    if (user === null) {
      LocalCache.remove('profile');
    } else {
      LocalCache.set('profile', user.toApi());
    }
  },
  storeUserProfile({ commit }, userProfile) {
    commit(types.UPDATE_USER_PROFILE, userProfile);
  },
  async updateUserProfile({ commit }, userProfile) {
    commit(types.UPDATE_USER_PROFILE, userProfile);
    if (userProfile == null) return;
    try {
      const userSettings = userProfile.getUserSettings();
      const promises = userSettings.map((x) => API.instance.postUserSetting({ instances: x }));
      await Promise.all(promises);
    } catch (error) {
      const msg = i18n.global.t('messages.failedToSaveUserSettings');
      ErrorHandler.showError(msg, error);
    }
  },
  getMembership({ commit }) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);
      API.instance.getMembership(
        (membership) => {
          commit(types.UPDATE_MEMBERSHIP, membership);
          LocalCache.set('membership', membership);
          resolve(membership);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetMembership');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },
  updateUserRole({ commit }, userRole) {
    commit(types.UPDATE_USER_ROLE, userRole);
    if (userRole === null) {
      LocalCache.remove('userRole');
    } else {
      LocalCache.set('userRole', userRole);
    }
  },
  updateMembership({ commit }, membership) {
    commit(types.UPDATE_MEMBERSHIP, membership);
    if (membership === null) {
      LocalCache.remove('membership');
    } else {
      LocalCache.set('membership', membership);
    }
  },
  updateIsNewVersionAvailable({ commit }, isNewVersionAvailable) {
    commit(types.UPDATE_IS_NEW_VERSION_AVAILABLE, isNewVersionAvailable);
  },
  updateIsPublicRoute({ commit }, isPublicRoute) {
    commit(types.UPDATE_IS_PUBLIC_ROUTE, isPublicRoute);
  },
  updateToken({ commit }, token) {
    commit(types.UPDATE_TOKEN, token);
    if (token === null) {
      LocalCache.remove('token');
    } else {
      LocalCache.set('token', token);
    }
  },
  updateRefreshToken({ commit }, refreshToken) {
    commit(types.UPDATE_REFRESH_TOKEN, refreshToken);
    if (refreshToken === null) {
      LocalCache.remove('refreshToken');
    } else {
      LocalCache.set('refreshToken', refreshToken);
    }
  },
  updateFreshdeskToken({ commit }, freshdeskToken) {
    commit(types.UPDATE_FRESHDESK_TOKEN, freshdeskToken);
    if (freshdeskToken === null) {
      LocalCache.remove('freshdeskToken');
    } else {
      LocalCache.set('freshdeskToken', freshdeskToken);
    }
  },
  markAsAuthenticated({ commit }) {
    commit(types.MARK_AS_AUTHENTICATED);
  },
  changePassword({ commit }, { currentPassword, newPassword }) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);
      API.instance.changePassword(
        currentPassword,
        newPassword,
        (response) => {
          if ('success' in response) {
            if (response.success) {
              resolve(response);
              GlobalEventBus.$emit(GlobalEvents.loaded);
              return;
            }
          }
          reject(response);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (errorResponse) => {
          reject(errorResponse);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },
  setChosenMembershipType({ commit }, type) {
    commit(types.SET_CHOSEN_MEMBERSHIP_TYPE, type);
    LocalCache.set('chosenMembershipType', type);
  },
  chooseOrganizationType({ commit }) {
    commit(types.SET_CHOSEN_MEMBERSHIP_TYPE, 'org');
    LocalCache.set('chosenMembershipType', 'org');
  },
  chooseExchangeType({ commit }) {
    commit(types.SET_CHOSEN_MEMBERSHIP_TYPE, 'exch');
    LocalCache.set('chosenMembershipType', 'exch');
  },
  clearMembershipType({ commit }) {
    commit(types.SET_CHOSEN_MEMBERSHIP_TYPE, null);
    LocalCache.remove('chosenMembershipType');
  },
  updateAppOffline({ commit }, isAppOffline) {
    commit(types.UPDATE_IS_APP_OFFLINE, isAppOffline);
  },
  toggleDebugMode({ commit, state, getters }) {
    if (!getters.isGlobalAdmin) {
      commit(types.SET_DEBUG_MODE, false);
    } else {
      commit(types.SET_DEBUG_MODE, !state.debugMode);
    }

    const togglePermissions = (permissions) => {
      if (state.debugMode) {
        Object.keys(permissions)
          .filter((x) => x.startsWith('can'))
          .forEach((x) => {
            permissions[x] = true;
          });
      } else {
        permissions = new Permissions(permissions.permissions);
      }
      return permissions;
    };

    if (state.chosenMembershipType === 'org') {
      let { permissions } = state.orgApp;
      rootStore.dispatch('orgApp/clearPermissions');
      permissions = togglePermissions(permissions);
      rootStore.dispatch('orgApp/updatePermissions', permissions);
    } else if (state.chosenMembershipType === 'exch') {
      let { permissions } = state.exchApp;
      rootStore.dispatch('exchApp/clearPermissions');
      permissions = togglePermissions(permissions);
      rootStore.dispatch('exchApp/updatePermissions', permissions);
    }
  },
  disableDebugMode({ commit }) {
    commit(types.SET_DEBUG_MODE, false);
  },
  updateBackgroundOperations({ commit }, backgroundOperations) {
    commit(types.UPDATE_BACKGROUND_OPERATIONS, backgroundOperations);
  },
};

export default actions;
